import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Auth } from "aws-amplify";
import classNames from "classnames";
import { useNavigate } from "react-router";
import { useFeatures } from "flagged";
import { downIcon, logoIcon, menuIcon } from "../../../assets/icons";
import { selectUser } from "../../../pages/UserProfile";
import BreadCrumb from "../BreadCrumb";
import { actions as appActions } from "../../../pages/Applications/applications.reducers";
import { actions as modalActions } from "../../organisms/Modal/modal.reducers";
import { actions as userActions } from "../../../pages/UserProfile/userProfile.reducers";
import {
  selectShouldShowMenuBar,
  selectShouldShowSidePanel,
} from "../../../pages/Applications";
import {
  EMPTY_STRING,
  CACHE_KEYS,
  MOUSE_EVENTS,
  ROUTES,
  ADMIN,
  GITHUB_AUTH_FLOW,
  IMPERSONATED_USER_SUB,
  INDEX,
} from "../../../constants";
import { MODALS } from "../../organisms/AppModals/modals.constants";
import { actions as authScreensActions } from "../../../pages/AuthScreens/authScreens.reducers";
import { profilePic } from "../../../assets/images";
import truncate from "lodash/truncate";
import { actions } from "../../../pages/DataModels/dataModels.reducers";
import { selectShouldShowLoadingPage } from "../../../pages/DataModels";

const { ADMIN_ORGANIZATION, X_REQUEST_ID, ORGANIZATION } = CACHE_KEYS;
const { MOUSE_DOWN } = MOUSE_EVENTS;
const {
  LOGIN,
  ORGANIZATIONS,
  HOME,
  USER_PROFILE,
  DEVELOPER_CONSOLE,
  SELECT_WORKSPACE,
} = ROUTES;
const { setShouldShowSidePanel } = appActions;
const { setSelectedModal, setShouldShowDeploymentModal } = modalActions;
const { MANAGE_BILLING_MODAL } = MODALS;
const { setShouldShowNav } = authScreensActions;
const { GITHUB, GITHUB_USER_INFO, PROVIDER } = GITHUB_AUTH_FLOW;
const { THIRD } = INDEX;
const { setShouldShowLoadingPage } = actions;
const { setIsAuthenticated } = userActions;

const Navbar = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { showBillings, showDeveloperConsole, showOrganizations } =
    useFeatures();

  const [menuBtnClicked, setMenuBtnClicked] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    name: EMPTY_STRING,
    organizations: { name: EMPTY_STRING },
    profilePic: EMPTY_STRING,
  });
  const [id, setId] = useState();

  const userProfile = useSelector(selectUser);
  const shouldShowSidePanel = useSelector(selectShouldShowSidePanel);
  const showMenuBar = useSelector(selectShouldShowMenuBar);
  const isLoadingPage = useSelector(selectShouldShowLoadingPage);

  const organization = JSON.parse(sessionStorage.getItem(ORGANIZATION));

  useEffect(() => {
    if (userProfile) setCurrentUser(userProfile);
  }, [userProfile]);

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (menuBtnClicked && ref.current && !ref.current.contains(e.target))
        setMenuBtnClicked(false);
    };
    document.addEventListener(MOUSE_DOWN, checkIfClickedOutside);
    return () =>
      document.removeEventListener(MOUSE_DOWN, checkIfClickedOutside);
  }, [menuBtnClicked]);

  useEffect(() => {
    window.onpopstate = (event) => setMenuBtnClicked(false);
  }, [menuBtnClicked]);

  useEffect(() => {
    const urlArray = window.location.pathname.toString().split("/");
    setId(urlArray[THIRD]);
    if (!isLoadingPage && id) dispatch(setShouldShowLoadingPage(false));
  }, [dispatch, isLoadingPage, id]);

  const handleSignOut = useCallback(() => {
    sessionStorage.removeItem(X_REQUEST_ID);
    sessionStorage.removeItem(IMPERSONATED_USER_SUB);
    sessionStorage.removeItem(ORGANIZATION);
    sessionStorage.removeItem(ADMIN_ORGANIZATION);
    dispatch(setShouldShowDeploymentModal(false));
    dispatch(setIsAuthenticated(false));
    window.location.assign(LOGIN);
  }, [dispatch]);

  const signOut = () => {
    const provider = sessionStorage.getItem(PROVIDER);
    localStorage.clear();
    if (provider === GITHUB) {
      sessionStorage.removeItem(PROVIDER);
      sessionStorage.removeItem(GITHUB_USER_INFO);
      handleSignOut();
    } else {
      const promise = Promise.resolve(Auth.signOut());
      promise.then(() => handleSignOut()).catch((err) => console.log(err));
    }
  };

  const switchToAdmin = () => {
    const { href } = window.location;
    sessionStorage.removeItem(IMPERSONATED_USER_SUB);
    const adminOrg = sessionStorage.getItem(ADMIN_ORGANIZATION);
    sessionStorage.setItem(ORGANIZATION, adminOrg);
    sessionStorage.removeItem(ADMIN_ORGANIZATION);
    window.location.reload(href);
  };
  const isImpersonating = Boolean(
    sessionStorage.getItem(IMPERSONATED_USER_SUB)
  );
  const isAdmin = Boolean(userProfile?.role === ADMIN);

  const handleSwitchWorkspaceClick = () => {
    if (isImpersonating) return;
    sessionStorage.removeItem(ORGANIZATION);
    sessionStorage.removeItem(ADMIN_ORGANIZATION);
    dispatch(setShouldShowNav(false));
    navigate(SELECT_WORKSPACE, { replace: true });
  };

  return (
    <nav className="flex sticky top-0 z-50 lg:z-[60] items-center bg-gray-900 border-b border-gray-800">
      <div className="border-r border-gray-800 self-stretch flex lg:hidden">
        <button
          className="h-full flex items-center px-4 hover:bg-gray-800/20"
          onClick={() => dispatch(setShouldShowSidePanel(!shouldShowSidePanel))}
        >
          <img src={menuIcon} alt="menu-icon" className="w-[30px]" />
        </button>
      </div>
      <div
        className={classNames(
          "logo lg:w-72 px-6 py-3.5 border-r border-gray-800",
          {
            "lg:w-[22.8rem]": showMenuBar,
          }
        )}
      >
        <div
          className="flex items-center cursor-pointer w-24"
          onClick={() => {
            navigate(HOME);
          }}
        >
          <img src={logoIcon} alt="logo-icon" className="w-[33px]" />
          <span className="text-2xl font-bold text-white ml-2">Tava</span>
        </div>
      </div>
      <div className="flex pr-4 py-2 flex-1">
        <BreadCrumb />
        <div className="user ml-auto flex-grow flex justify-end">
          {isImpersonating && (
            <div className="thumb mr-4">
              <button
                className="btn bg-[#374151] flex-grow block text-center text-md  font-semibold text-white  hover:bg-[#516078] h-fit"
                onClick={switchToAdmin}
              >
                Switch To Admin
              </button>
            </div>
          )}
          <div className="thumb mr-4">
            <img
              src={
                currentUser.profilePicUrl
                  ? currentUser.profilePicUrl
                  : profilePic
              }
              className="rounded-full w-10 h-10 cursor-pointer"
              alt="profile-pic"
              onClick={() => {
                navigate(USER_PROFILE);
              }}
            />
          </div>
          <div className="mr-4 hidden sm:block max-w-[25%]">
            <h6
              className="text-base leading-6 font-medium text-white truncate ..."
              title={currentUser.name}
            >
              {currentUser.name}
            </h6>
            <span
              className="text-sm leading-5 font-medium block text-gray-400 truncate ..."
              title={organization?.name}
            >
              {organization?.name}
            </span>
          </div>
          <div className="relative" ref={ref}>
            <button
              className="h-10 w-10 grid place-items-center bg-gray-800 hover:bg-gray-800/75 rounded-md"
              onClick={() => setMenuBtnClicked(!menuBtnClicked)}
            >
              <img
                src={downIcon}
                alt="down-icon"
                className={classNames("w-6", { "-rotate-180": menuBtnClicked })}
              />
            </button>
            <div
              id="sortboxmenu"
              className={classNames(
                "absolute z-50 right-1 top-full min-w-max shadow rounded-lg bg-gray-800 border-[1px] border-gray-400 transition delay-75 ease-in-out mt-6",
                {
                  hidden: !menuBtnClicked,
                }
              )}
            >
              <div className="block w-[324px] text-left text-gray-400 bg-gray-900 rounded-lg">
                <div
                  className="flex flex-row items-center h-[44px] hover:bg-gray-600 hover:rounded-t-lg pl-2"
                  title={organization?.name}
                >
                  <a
                    href={HOME}
                    className="block px-3 py-2 flex-grow font-semibold text-[#6B7280]"
                  >
                    {truncate(organization?.name, { length: 35 })}
                  </a>
                </div>
                <div className="flex flex-row items-center h-[44px] hover:bg-gray-600 pl-2">
                  <a
                    href={HOME}
                    className="flex-grow block px-3 py-2 font-semibold"
                  >
                    Account
                  </a>
                </div>
                {showBillings && (
                  <div className="flex flex-row items-center h-[44px] hover:bg-gray-600 pl-2 cursor-pointer">
                    <p
                      className="block px-3 py-2 flex-grow font-semibold cursor-pointer"
                      onClick={() =>
                        dispatch(setSelectedModal(MANAGE_BILLING_MODAL))
                      }
                    >
                      Billing
                    </p>
                  </div>
                )}
                <div className="w-full bg-gray-700 h-px"></div>
                {isAdmin && showOrganizations && (
                  <div className="flex flex-row items-center h-[44px] hover:bg-gray-600 pl-2 cursor-pointer">
                    <span
                      onClick={() => {
                        navigate(ORGANIZATIONS);
                        setMenuBtnClicked(!menuBtnClicked);
                      }}
                      className="block px-3 py-2 flex-grow font-semibold"
                    >
                      Admin
                    </span>
                  </div>
                )}
                <div
                  className={classNames(
                    "flex flex-row items-center h-[44px] pl-2",
                    {
                      "cursor-not-allowed text-gray-600 hover:bg-gray-800":
                        isImpersonating,
                      "cursor-pointer hover:bg-gray-600": !isImpersonating,
                    }
                  )}
                >
                  <span
                    className="block px-3 py-2 flex-grow font-semibold"
                    onClick={() => handleSwitchWorkspaceClick()}
                  >
                    Switch Workspace
                  </span>
                </div>
                {showDeveloperConsole && (
                  <div className="flex flex-row items-center h-[44px] hover:bg-gray-600 pl-2 cursor-pointer">
                    <span
                      onClick={() => {
                        navigate(DEVELOPER_CONSOLE);
                        setMenuBtnClicked(!menuBtnClicked);
                      }}
                      className="block px-3 py-2 flex-grow font-semibold"
                    >
                      Developer Console
                    </span>
                  </div>
                )}
                <div className="flex flex-row items-center h-[44px] hover:bg-gray-600 pl-2">
                  <p
                    onClick={() => {
                      navigate(USER_PROFILE);
                      setMenuBtnClicked(!menuBtnClicked);
                    }}
                    className="block px-3 py-2 flex-grow font-semibold cursor-pointer"
                  >
                    Profile
                  </p>
                </div>
                <div className="flex flex-row items-center h-[44px] hover:bg-gray-600 pl-2 hover:rounded-b-lg cursor-pointer">
                  <span
                    onClick={() => signOut()}
                    className="block px-3 py-2 flex-grow font-semibold"
                  >
                    Sign out
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
